import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { Product, formatPrice } from '../data/products';
import { CarHeartIcon, CarCartIcon } from './CarIcons';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { t } = useTranslation();
  const { addToCart, toggleFavorite, favorites } = useStore();
  const isFav = favorites.includes(product.id);
  const discount = product.regularPrice > 0 && product.salePrice > 0
    ? Math.round((1 - product.salePrice / product.regularPrice) * 100)
    : 0;

  const displayPrice = product.salePrice > 0 ? product.salePrice : product.regularPrice;

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-[#E31E24]/10 transition-all duration-500 hover:-translate-y-1 flex flex-col">
      {/* Image */}
      <Link to={`/urun/${product.id}`} className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 p-4 overflow-hidden block">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%23f8f9fa"/><stop offset="100%" style="stop-color:%23e9ecef"/></linearGradient></defs><rect fill="url(%23bg)" width="200" height="200" rx="8"/><circle cx="100" cy="85" r="35" fill="%23dee2e6"/><ellipse cx="100" cy="85" rx="20" ry="12" fill="%23ced4da"/><path d="M60 130 Q100 110 140 130 Q140 160 100 170 Q60 160 60 130" fill="%23dee2e6"/><text x="100" y="190" text-anchor="middle" fill="%236c757d" font-size="11" font-family="Arial,sans-serif" font-weight="bold">T-RaX</text></svg>`)}`;
          }}
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {!product.inStock && (
            <span className="bg-gray-800 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
              Tükendi
            </span>
          )}
          {product.inStock && product.stock <= 10 && (
            <span className="bg-amber-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
              Son {product.stock} adet!
            </span>
          )}
          {discount > 0 && (
            <span className="bg-[#E31E24] text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
              %{discount} {t('home.sale')}
            </span>
          )}
          {product.isNew && (
            <span className="bg-[#FF6B00] text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
              {t('home.new')}
            </span>
          )}
        </div>

        {/* Favorite Button */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
          className={`absolute top-3 right-3 p-2 rounded-full shadow-lg transition-all duration-300 ${
            isFav
              ? 'bg-[#E31E24] text-white scale-110'
              : 'bg-white/90 text-gray-400 hover:text-[#E31E24] hover:bg-white hover:scale-110'
          }`}
          title={isFav ? t('home.removeFromFav') : t('home.addToFav')}
        >
          <CarHeartIcon size={18} />
        </button>
      </Link>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 mb-2 group-hover:text-[#E31E24] transition-colors min-h-[40px]">
          {product.name}
        </h3>

        {/* Rating */}
        {product.rating > 0 && (
          <div className="flex items-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                className={`w-3 h-3 ${star <= Math.round(product.rating) ? 'text-amber-400' : 'text-gray-200'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
            <span className="text-[10px] text-gray-400 ml-1">({product.reviewCount})</span>
          </div>
        )}

        <div className="mt-auto">
          {/* Price */}
          <div className="flex items-baseline gap-2 mb-3">
            {displayPrice > 0 ? (
              <>
                <span className="text-lg font-black text-[#E31E24]">{formatPrice(displayPrice)} ₺</span>
                {discount > 0 && (
                  <span className="text-xs text-gray-400 line-through">{formatPrice(product.regularPrice)} ₺</span>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-400">Fiyat bilgisi için iletişime geçin</span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-1.5 sm:gap-2">
            <button
              onClick={() => toggleFavorite(product.id)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 py-2 px-1.5 sm:px-2 text-[10px] sm:text-xs font-bold rounded-xl transition-all duration-300 active:scale-95 ${
                isFav
                  ? 'bg-[#E31E24] text-white shadow-lg shadow-[#E31E24]/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-[#E31E24]/10 hover:text-[#E31E24]'
              }`}
            >
              <CarHeartIcon size={14} className="shrink-0" />
              <span className="truncate">{isFav ? t('home.removeFromFav') : t('home.addToFav')}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if (product.inStock) addToCart(product); }}
              disabled={!product.inStock}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 py-2 px-1.5 sm:px-2 text-[10px] sm:text-xs font-bold rounded-xl transition-all duration-300 active:scale-95 ${
                !product.inStock
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white hover:shadow-lg hover:shadow-[#E31E24]/30'
              }`}
            >
              <CarCartIcon size={14} className="shrink-0" />
              <span className="truncate">{!product.inStock ? 'Tükendi' : t('home.addToCart')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
